import { useCallback, useState } from 'react';
import {
  isBlockedFileName,
  MAX_FILE_SIZE,
  MAX_FILES_PER_MESSAGE,
  formatFileSize,
  type FileAttachment,
} from '@avslack/shared';
import { apiFetch, ApiRequestError, uploadFilePreview } from '../lib/api';
import { makePreview, wantsPreview } from '../lib/file-preview';

export interface PendingUpload {
  key: string;
  name: string;
  size: number;
  mime: string;
  progress: number;
  error: string | null;
  attachment: FileAttachment | null;
  /** Kepeknel helyi elonezet, hogy azonnal latszodjon valami. */
  previewUrl: string | null;
}

interface UploadTicket {
  fileId: string;
  uploadUrl: string;
  contentType: string;
}

/**
 * Feltoltes elorehaladas-jelzessel.
 *
 * A `fetch` nem ad feltoltesi elorehaladast, ezert itt XHR-t hasznalunk: egy
 * 80 MB-os videonal a nema varakozas hasznalhatatlan lenne.
 */
function putWithProgress(
  url: string,
  contentType: string,
  file: File,
  onProgress: (ratio: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.setRequestHeader('content-type', contentType);

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });

    request.addEventListener('load', () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`A tároló elutasította (HTTP ${request.status}).`)),
    );
    request.addEventListener('error', () => reject(new Error('A feltöltés megszakadt.')));
    request.addEventListener('abort', () => reject(new Error('A feltöltés megszakadt.')));

    request.send(file);
  });
}

/** A helyi ellenorzes csak kenyelmi: az igazi szuro a szerveren van. */
function validate(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `Túl nagy (${formatFileSize(file.size)}), a határ ${formatFileSize(MAX_FILE_SIZE)}.`;
  }
  if (isBlockedFileName(file.name)) {
    return 'Ez a fájltípus biztonsági okból nem tölthető fel. Tömörítsd előbb.';
  }
  return null;
}

export function useUploads() {
  const [uploads, setUploads] = useState<PendingUpload[]>([]);

  const patch = useCallback((key: string, changes: Partial<PendingUpload>) => {
    setUploads((current) => current.map((u) => (u.key === key ? { ...u, ...changes } : u)));
  }, []);

  const add = useCallback(
    async (incoming: File[]) => {
      const accepted = incoming.slice(0, MAX_FILES_PER_MESSAGE);

      const entries: PendingUpload[] = accepted.map((file, index) => ({
        key: `${Date.now()}-${index}-${file.name}`,
        name: file.name,
        size: file.size,
        mime: file.type || 'application/octet-stream',
        progress: 0,
        error: validate(file),
        attachment: null,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      }));

      setUploads((current) => [...current, ...entries].slice(0, MAX_FILES_PER_MESSAGE));

      await Promise.all(
        entries.map(async (entry, index) => {
          if (entry.error) return;
          const file = accepted[index]!;
          // Az elonezet a feltoltessel PARHUZAMOSAN keszul - ne nyujtsa meg.
          const preview = wantsPreview(entry.mime) ? makePreview(file) : null;

          try {
            const ticket = await apiFetch<UploadTicket>('/api/files/upload-url', {
              method: 'POST',
              body: JSON.stringify({ name: entry.name, mime: entry.mime, size: entry.size }),
            });

            await putWithProgress(ticket.uploadUrl, ticket.contentType, file, (ratio) =>
              patch(entry.key, { progress: Math.round(ratio * 95) }),
            );

            const done = await apiFetch<{ file: FileAttachment }>(
              `/api/files/${ticket.fileId}/complete`,
              { method: 'POST' },
            );

            let attachment = done.file;
            /*
              Az elonezet NEM akadalyozhatja a kuldest: ha nem sikerul, a fajl
              ikonnal megy ki, ugyanugy, mint eddig.
            */
            const image = preview ? await preview : null;
            if (image && !attachment.thumbUrl) {
              attachment = await uploadFilePreview(attachment.id, image).catch(() => attachment);
            }

            patch(entry.key, { progress: 100, attachment });
          } catch (error) {
            patch(entry.key, {
              error:
                error instanceof ApiRequestError
                  ? error.message
                  : error instanceof Error
                    ? error.message
                    : 'A feltöltés nem sikerült.',
            });
          }
        }),
      );
    },
    [patch],
  );

  const remove = useCallback((key: string) => {
    setUploads((current) => {
      const target = current.find((u) => u.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((u) => u.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    setUploads((current) => {
      for (const upload of current) {
        if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
      }
      return [];
    });
  }, []);

  const readyFileIds = uploads
    .map((u) => u.attachment?.id)
    .filter((id): id is string => Boolean(id));

  return {
    uploads,
    add,
    remove,
    clear,
    readyFileIds,
    /** Amig van folyamatban levo feltoltes, a kuldes varjon. */
    isBusy: uploads.some((u) => !u.attachment && !u.error),
    hasAny: uploads.length > 0,
  };
}

/**
 * Egyetlen kep feltoltese - profilkephez.
 *
 * Ugyanazt a ket lepest jarja be, mint a csatolmany (jegy keres, feltoltes,
 * lezaras), csak elorehaladas-jelzes es allapot nelkul: egy avatar par
 * kilobajt, ott a folyamatjelzo tobb zajt csinalna, mint hasznot.
 */
export async function uploadSingleImage(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Csak képfájl tölthető fel.');
  }

  const problem = validate(file);
  if (problem) throw new Error(problem);

  const ticket = await apiFetch<UploadTicket>('/api/files/upload-url', {
    method: 'POST',
    body: JSON.stringify({ name: file.name, size: file.size, mime: file.type }),
  });

  await putWithProgress(ticket.uploadUrl, ticket.contentType, file, () => undefined);
  await apiFetch(`/api/files/${ticket.fileId}/complete`, { method: 'POST' });

  return ticket.fileId;
}
